import { inject, injectable } from 'inversify';
import React from 'react';

import { User } from '@project44/ui-utils';
import {
  DtoBase,
  TenantModels,
  UserModels,
} from '@project44-models/iam-gateway';

import { IAxiosApiState } from '../../../shared/core/domain/types';
import { TenantUIController } from '../../Tenant/infrastructure/controllers/Tenant.ui.controller';
import { UserTenantAgnosticRoles } from '../domain/aggregates/UserTenantAgnosticRoles.aggregate';
import { UserTenantSpecificRoles } from '../domain/aggregates/UserTenantSpecificRoles.aggregate';
import {
  IUserDetailsRoutePayload,
  TUserMoveFormComponent,
  TUseUserCreator,
} from '../domain/interfaces';
import { UserDetailsRoute } from '../domain/routes/UserDetails.route';
import { UserComponentFactory } from '../infrastructure/factories/User.component.factory';
import { UserFactory } from '../infrastructure/factories/User.factory';
import { TUserCreationFormView } from '../infrastructure/ui/UserCreatorForm/UserCreationForm.view';
import { UserUnifiedRolesTable } from '../infrastructure/ui/UserUnifiedRolesTable/UserUnifiedRolesTable.component';

@injectable()
export class UserUIService {
  constructor(
    @inject(UserFactory) private readonly userFactory: UserFactory,

    @inject(UserComponentFactory)
    private readonly userUIFactory: UserComponentFactory,

    @inject(TenantUIController)
    private readonly tenantUIController: TenantUIController,
  ) {}

  public readonly composeUserUnifiedRolesTenantAgnosticAggregate = (payload: {
    userPrincipal: User;
    user: DtoBase<UserModels.UserViewModel>;
    tenant: DtoBase<TenantModels.TenantViewModel>;
  }): UserTenantAgnosticRoles => {
    return this.userFactory.createUserTenantAgnosticRoles(payload);
  };

  public readonly composeUserUnifiedRolesTenantSpecificAggregate = (payload: {
    userPrincipal: User;
    user: DtoBase<UserModels.UserViewModel>;
    tenant: DtoBase<TenantModels.TenantViewModel>;
  }): UserTenantSpecificRoles => {
    return this.userFactory.createUserTenantSpecificRoles(payload);
  };

  public readonly composeUserUnifiedRolesTable = (): UserUnifiedRolesTable => {
    return this.userUIFactory.composeUserUnifiedRolesTable();
  };

  public readonly composeUserCreationForm = (payload: {
    userPrincipal: User;
    useUserCreatorConfig: ReturnType<TUseUserCreator>;
    afterUserCreationActionHandler?: (
      user: DtoBase<UserModels.UserViewModel>,
    ) => void;
  }): React.ReactElement<TUserCreationFormView> => {
    return this.userUIFactory.composeUserCreationForm(payload);
  };

  public readonly composeUserMoveForm = (payload: {
    userPrincipal: User;
    userToMove: DtoBase<UserModels.UserViewModel>;
    isApiRequestInProgress?: boolean;
    moveUserAxiosDataState: IAxiosApiState<unknown>;
    moveUserAxiosHandler: (payload: {
      userDto: DtoBase<UserModels.UserViewModel>;
      targetTenantUuid: string;
    }) => Promise<void>;
  }): React.ReactElement<
    Omit<TUserMoveFormComponent, 'UserMoveFormCreator'>
  > => {
    return this.userUIFactory.composeUserMoveForm({
      ...payload,
      TenantMover:
        this.tenantUIController.createUserMoveTenantSwitcherComponent,
    });
  };

  public readonly composeUserDetailsRoute = (payload: {
    userPrincipal: User;
    user: DtoBase<UserModels.UserViewModel>;
    tenant: DtoBase<TenantModels.TenantViewModel>;
    getUserDetails: IUserDetailsRoutePayload['getUserDetails'];
    updateUserDetails: IUserDetailsRoutePayload['updateUserDetails'];
    composeUseApiRequestWrapper: IUserDetailsRoutePayload['composeUseApiRequestWrapper'];
  }): UserDetailsRoute => {
    const {
      composeUseApiRequestWrapper,
      updateUserDetails,
      getUserDetails,
      user,
      userPrincipal,
      tenant,
    } = payload;

    const userRolesPayload = { user, tenant, userPrincipal };

    const userTenantAgnosticRoles =
      this.composeUserUnifiedRolesTenantAgnosticAggregate(userRolesPayload);

    const userTenantSpecificRoles =
      this.composeUserUnifiedRolesTenantSpecificAggregate(userRolesPayload);

    const userRolesAdapter =
      userTenantAgnosticRoles.unifiedRolesEditor.toUnifiedRoleCollectionDto;
    const userRolesEditor = userTenantAgnosticRoles.unifiedRolesEditor;

    const updateUserTenantAgnosticRoles =
      userTenantAgnosticRoles.updateUserTenantAgnosticRoles;

    const userDetailsRoutePayload: IUserDetailsRoutePayload = {
      updateUserTenantAgnosticRoles,
      getUserDetails,
      updateUserDetails,
      composeUseApiRequestWrapper,
      userRolesAdapter,
      userRolesEditor,
      getUserTenantAgnosticRoles:
        userTenantAgnosticRoles.getUserTenantAgnosticRoles,
      getUserTenantSpecificRoles:
        userTenantSpecificRoles.getUserTenantSpecificRoles,
      updateUserTenantSpecificRoles:
        userTenantSpecificRoles.updateUserTenantSpecificRoles,
      composeUserDetailsDto: this.userFactory.createUserDto,
      changeUserTenantSpecificAdminRolesType:
        userTenantSpecificRoles.changeUserTenantSpecificAdminRolesType,
    };

    return UserDetailsRoute.compose(userDetailsRoutePayload);
  };
}
